import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from "@angular/forms";
import {User} from "../../../../Models/user.model";
import {UserService} from "../../../../Services/user.service";
import {ClientService} from "../../../../Services/client.service";
import {NotificationService} from "../../../../Services/notification.service";
import {allControlsValidValidator, CreationItzModel, loginFormatValidator} from "../../../../Models/creation-itz.model";
import {Mission} from "../../../../Models/mission.model";
import {NgClass} from '@angular/common';
import {ToggleButtonModule} from 'primeng/togglebutton';
import {SelectModule} from 'primeng/select';
import {FormsModule} from '@angular/forms';

@Component({
    selector: 'user-information-tab',
    templateUrl: './user-information-tab.component.html',
    styleUrls: ['./user-information-tab.component.css'],
    imports: [ReactiveFormsModule, FormsModule, NgClass, ToggleButtonModule, SelectModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserInformationTabComponent implements OnChanges, OnInit {

  @Input() user: User;  // Input property to receive user data
  @Output() userUpdated: EventEmitter<User> = new EventEmitter<User>();
  @Output() resetPassword: EventEmitter<void> = new EventEmitter<void>();
  @Output() closeModal: EventEmitter<void> = new EventEmitter<void>();

  form: FormGroup<CreationItzModel>; // Reactive form group to handle user form
  missions: Mission[] = [];

  readonly countryOptions = [
    { label: 'Luxembourg', value: 'LUXEMBOURG', code: 'lu' },
    { label: 'France', value: 'FRANCE', code: 'fr' },
    { label: 'Belgique', value: 'BELGIQUE', code: 'be' },
    { label: 'Allemagne', value: 'ALLEMAGNE', code: 'de' }
  ];

  selectedCountryOption: { label: string; value: string; code: string } = { label: 'Luxembourg', value: 'LUXEMBOURG', code: 'lu' };

  constructor(private fb: FormBuilder, private userService: UserService, private clientService: ClientService, private notificationService: NotificationService, private cdr: ChangeDetectorRef) {
  }

  ngOnInit() {
    this.initializeForm();
    this.clientService.getClientList().subscribe(missions => {
      this.missions = missions;
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['user'] && this.user) {
      this.updateFormValues();  // Update form values when user input changes
    }
  }

  initializeForm() {
    this.form =
      new FormGroup<CreationItzModel>({
        firstName: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
        lastName: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
        userLogin: new FormControl({value: '', disabled: true}, {
          nonNullable: true,
          validators: [Validators.required, loginFormatValidator()]
        }),
        userMailAddress: new FormControl({value: '', disabled: true}, {
          nonNullable: true,
          validators: [Validators.required, Validators.email]
        }),
        userSenorityDate: new FormControl(new Date, {nonNullable: true, validators: [Validators.required]}),
        userRecordID: new FormControl(''),
        taxResidence: new FormControl('LUXEMBOURG', {nonNullable: true, validators: [Validators.required]}),
        userMission: new FormControl<Mission | null>(null),
        isBlocked: new FormControl(false, {nonNullable: true, validators: [Validators.required]}),
        isAdmin: new FormControl(false, {nonNullable: true, validators: [Validators.required]})
      }, {
        validators: allControlsValidValidator()
      })
    ;

    if (this.user) {
      // Initialize form with user data if available
      this.updateFormValues();
    }
  }

  updateFormValues() {
    if (this.form) {
      this.form.patchValue({
        isAdmin: this.user.userProfile === 'ADMIN',
        firstName: this.user.userFirstName,
        lastName: this.user.userName,
        userLogin: this.user.userLogin,
        userMailAddress: this.user.userMailAddress,
        userSenorityDate: this.user.userSenorityDate ?? new Date(),
        userRecordID: this.user.recordId,
        taxResidence: this.user.taxResidence || 'LUXEMBOURG',
        userMission: this.user.mission ?? null,
        isBlocked: this.user.userStatus === 'INACTIVE'
      });
      this.selectedCountryOption = this.countryOptions.find(o => o.value === this.user.taxResidence) ?? this.countryOptions[0];
      if (!this.user.id) {
        this.form.controls.userLogin.enable();
        this.form.controls.userMailAddress.enable();
      }
    }
  }

  private normalizeLoginPart(value: string): string {
    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '');
  }


  itzInitialisation() {
    if (this.form) {
      const firstName = this.normalizeLoginPart(this.form.controls.firstName.value?.toLowerCase() || '');
      const lastName = this.normalizeLoginPart(this.form.controls.lastName.value?.toLowerCase() || '');
      this.form.controls.userMailAddress.patchValue(`${firstName}.${lastName}@xpertiz.lu`);
      this.form.controls.userLogin.patchValue(`${firstName}.${lastName}`);
      this.updateUser();
    }
  }

  resetUserPwd() {
    this.resetPassword.emit();
  }

  updateUser() {
    if (this.user && this.form) {
      this.user.userFirstName = this.form.controls.firstName.value;
      this.user.userName = this.form.controls.lastName.value;
      this.user.userLogin = this.form.controls.userLogin.value;
      this.user.userMailAddress = this.form.controls.userMailAddress.value;
      this.user.mission = this.form.controls.userMission.value;
      this.user.userSenorityDate = this.form.controls.userSenorityDate.value;
      this.user.recordId = this.form.controls.userRecordID.value || '';
      this.user.taxResidence = this.form.controls.taxResidence.value || '';
      this.user.userProfile = this.form.controls.isAdmin.value ? 'ADMIN' : 'EMPLOYEE';
      this.user.userStatus = this.form.controls.isBlocked.value ? 'INACTIVE' : 'ACTIVE';

      // Check if balances is null and initialize if necessary
      if (this.user.balances == null) {
        this.user.balances = [];
      }
    }
  }

  onCountryChange() {
    this.form.controls.taxResidence.setValue(this.selectedCountryOption?.value ?? '');
    this.updateUser();
  }

  updateOrNewUser() {
    if (this.form.status === "VALID" && this.user) {
      this.updateUser();
      this.form.markAsUntouched();
      this.userUpdated.emit(this.user);
      this.closeModal.emit();
    }
  }

  private controlHasErrorAndTouched(controlName: keyof CreationItzModel, errorType: string): boolean {
    const control = this.form.controls[controlName];
    return !!control?.errors?.[errorType] && control.touched;
  }

  private controlIsRequiredErrorAndTouched(controlName: keyof CreationItzModel): boolean {
    return this.controlHasErrorAndTouched(controlName, 'required');
  }

  isFirstNameRequiredError(): boolean {
    return this.controlIsRequiredErrorAndTouched('firstName');
  }

  isLastNameRequiredError(): boolean {
    return this.controlIsRequiredErrorAndTouched('lastName');
  }

  isUserLoginRequiredError(): boolean {
    return this.controlIsRequiredErrorAndTouched('userLogin');
  }

  isUserMailAddressRequiredError(): boolean {
    return this.controlIsRequiredErrorAndTouched('userMailAddress');
  }

  compareClients(a: Mission | null, b: Mission | null): boolean {
    if (!a || !b) return a === b;
    return a.code === b.code;
  }

  isUserMissionRequiredError(): boolean {
    return this.controlIsRequiredErrorAndTouched('userMission');
  }

  isUserMailAddressFormatError(): boolean {
    return this.controlHasErrorAndTouched('userMailAddress', 'email');
  }

  isUserLoginFormatError(): boolean {
    return this.controlHasErrorAndTouched('userLogin', 'invalidLoginFormat');
  }

  isSenorityDateRequiredError(): boolean {
    return this.controlIsRequiredErrorAndTouched('userSenorityDate');
  }
}
